// Localização: frontend/src/components/Assinatura/ModalCancelamentoAssinatura.jsx
// Modal de cancelamento com questionário sobre o motivo

import React, { useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';

const ModalCancelamentoAssinatura = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    isPending = false,
    isLoading = false 
}) => {
    const [motivoSelecionado, setMotivoSelecionado] = useState('');
    const [outroMotivo, setOutroMotivo] = useState('');
    const [mostrarConfirmacaoFinal, setMostrarConfirmacaoFinal] = useState(false);

    // Opções de motivos de cancelamento
    const motivos = [
        { id: 'preco', label: 'O preço está muito alto' },
        { id: 'recursos', label: 'Não encontrei os recursos que preciso' },
        { id: 'dificuldade', label: 'Estou tendo dificuldade para usar a plataforma' },
        { id: 'nao_uso', label: 'Não estou usando o suficiente' },
        { id: 'alternativa', label: 'Encontrei uma alternativa melhor' },
        { id: 'problemas_tecnico', label: 'Problemas técnicos frequentes' },
        { id: 'atendimento', label: 'Insatisfação com o atendimento' },
        { id: 'outro', label: 'Outro motivo' }
    ];

    const handleMotivoChange = (motivoId) => {
        setMotivoSelecionado(motivoId);
        if (motivoId !== 'outro') {
            setOutroMotivo('');
        }
    };

    const handleContinuar = () => {
        if (!motivoSelecionado) {
            return;
        }
        if (motivoSelecionado === 'outro' && !outroMotivo.trim()) {
            return;
        }
        setMostrarConfirmacaoFinal(true);
    };

    const handleConfirmarCancelamento = () => {
        const motivoCompleto = motivoSelecionado === 'outro' 
            ? outroMotivo 
            : motivos.find(m => m.id === motivoSelecionado)?.label || motivoSelecionado;
        
        onConfirm(motivoCompleto);
    };

    const handleClose = () => {
        if (!isLoading) {
            setMotivoSelecionado('');
            setOutroMotivo('');
            setMostrarConfirmacaoFinal(false);
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title=""
            size="lg"
            hideHeader={true}
        >
            <div className="relative">
                {/* Botão fechar customizado */}
                <button
                    onClick={handleClose}
                    disabled={isLoading}
                    className="absolute top-0 right-0 text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-higeia-accent rounded-full p-1 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Fechar modal"
                >
                    <X size={24} />
                </button>

                {!mostrarConfirmacaoFinal ? (
                    // Tela 1: Questionário de motivos
                    <div className="space-y-6">
                        {/* Header */}
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <AlertTriangle className="h-8 w-8 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Cancelar Assinatura
                            </h2>
                            <p className="text-gray-600">
                                Gostaríamos de entender o motivo do cancelamento para melhorarmos nossos serviços.
                            </p>
                        </div>

                        {/* Questionário */}
                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Qual é o principal motivo do cancelamento? *
                            </label>
                            
                            <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                                {motivos.map((motivo) => (
                                    <label
                                        key={motivo.id}
                                        className={`
                                            flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all
                                            ${motivoSelecionado === motivo.id
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                            }
                                        `}
                                    >
                                        <input
                                            type="radio"
                                            name="motivo"
                                            value={motivo.id}
                                            checked={motivoSelecionado === motivo.id}
                                            onChange={() => handleMotivoChange(motivo.id)}
                                            className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                        />
                                        <span className="ml-3 text-gray-700 flex-1">
                                            {motivo.label}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {/* Campo de texto para "Outro motivo" */}
                            {motivoSelecionado === 'outro' && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Por favor, descreva o motivo:
                                    </label>
                                    <textarea
                                        value={outroMotivo}
                                        onChange={(e) => setOutroMotivo(e.target.value)}
                                        placeholder="Descreva o motivo do cancelamento..."
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                                        rows="4"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Botão Continuar */}
                        <div className="flex gap-3 pt-4 border-t border-gray-200">
                            <button
                                onClick={handleClose}
                                disabled={isLoading}
                                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleContinuar}
                                disabled={!motivoSelecionado || (motivoSelecionado === 'outro' && !outroMotivo.trim()) || isLoading}
                                className="flex-1 px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Continuar
                            </button>
                        </div>
                    </div>
                ) : (
                    // Tela 2: Confirmação final
                    <div className="space-y-6">
                        {/* Header */}
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <AlertTriangle className="h-8 w-8 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Realmente deseja deixar o Higeia?
                            </h2>
                            <p className="text-gray-600">
                                Temos certeza de que podemos ajudar você a ter uma experiência melhor.
                            </p>
                        </div>

                        {/* Informações sobre o cancelamento */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                                <strong>Importante:</strong> Sua assinatura permanecerá ativa até o final do período pago. 
                                Você não perderá acesso imediatamente.
                            </p>
                        </div>

                        {/* Motivo selecionado (resumo) */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Motivo informado:</p>
                            <p className="text-sm font-medium text-gray-900">
                                {motivoSelecionado === 'outro' 
                                    ? outroMotivo 
                                    : motivos.find(m => m.id === motivoSelecionado)?.label
                                }
                            </p>
                        </div>

                        {/* Botões de ação */}
                        <div className="flex gap-3 pt-4 border-t border-gray-200">
                            <button
                                onClick={() => setMostrarConfirmacaoFinal(false)}
                                disabled={isLoading}
                                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleConfirmarCancelamento}
                                disabled={isLoading}
                                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Cancelando...
                                    </>
                                ) : (
                                    'Sim, cancelar assinatura'
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ModalCancelamentoAssinatura;

